import { useState, useEffect, useRef, useCallback, Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { 
  Building2, User, Shield, Loader2, Eye, EyeOff, 
  ArrowRight, Users, Scale, FileSearch, MessageSquare, MessageCircle,
  ChevronRight, Play, Clock,
  ShieldCheck, Handshake, BarChart3, Gavel,
  Home, Award, Target, MapPin, Lock, Globe,
  Video, BookOpen, Newspaper, ChevronDown,
  Moon, Sun, Briefcase,
  ChevronUp, Search, Plus, Calculator, GitCompare, TrendingUp,
  AlertTriangle, Wrench, CheckCircle, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Section content routing (all lazy imports live here)
import { getSectionContent } from '@/pages/sectionRoutes';

// Project Search Bar (for landing page)
import ProjectSearchBar from '@/components/ProjectSearchBar';

// Regular imports (always needed)
import FindAddProject from '@/components/FindAddProject';
import RegistrationForm from '@/components/auth/RegistrationForm';
import Sidebar from '@/components/layout/SidebarNew';
import MobileBottomNav from '@/components/layout/MobileBottomNavNew';
import DeveloperDashboard from '@/components/DeveloperDashboard';
import ResumeBanner from '@/components/ResumeBanner';
import PendingProofBanner from '@/components/PendingProofBanner';
import CorpusPulseBanner from '@/components/CorpusPulseBanner';
import { DEFAULT_PROJECT_ID } from '@/lib/navTree';
import { EvidenceStrip } from '@/components/home/EvidenceStrip';
import { 
  AnimatedStat, 
  ScrollToTopButton, VideoPlayerModal, ArticleViewerModal, NewsViewerModal,
  OurStancePanel
} from '@/components/landing';
import { SectionLoader } from '@/components/loading';

// Import constants and hooks
import { buyerBenefits, developerBenefits, publicSections, privateSections } from '@/lib/constants';
import { useDarkMode } from '@/hooks/useDarkMode';
import { useScrollNavigation } from '@/hooks/useScrollNavigation';
import { useLayoutPref } from '@/hooks/useLayoutPref';
import { trackView } from '@/lib/trackView';
import { SECTION_REDIRECTS, sectionLabel } from '@/lib/navTree';
import { SECTION_LABELS } from '@/lib/sectionLabels';
import { useSwipeNav, SwipeHint } from '@/lib/useSwipeNav';
import { useSectionHistory } from '@/lib/useSectionHistory';
import SectionTrail from '@/components/layout/SectionTrail';
import EthosVerbs from '@/components/ethos/EthosVerbs';
import IsIsntCard from '@/components/ethos/IsIsntCard';
import EthosAckGate from '@/components/ethos/EthosAckGate';
import WelcomeChecklist from '@/components/ethos/WelcomeChecklist';
import FirstVisitSurvey from '@/components/ethos/FirstVisitSurvey';
import { ETHOS_TAGLINE, ETHOS_SUBLINE } from '@/lib/ethosCopy';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon mappings for benefits sections (needed because constants use strings)
const benefitIcons = { Users, FileSearch, Scale, MessageSquare, ShieldCheck, Award, Handshake, BarChart3 };

// Check if user is a developer
const isDeveloper = (user) => user?.role === 'developer';

// Google brand mark for the OAuth button
const GoogleIcon = () => (
  <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47a5.53 5.53 0 0 1-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"/>
    <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09A11.99 11.99 0 0 0 12 24z"/>
    <path fill="#FBBC05" d="M5.27 14.29A7.2 7.2 0 0 1 4.89 12c0-.8.14-1.57.38-2.29V6.62H1.29a12 12 0 0 0 0 10.76l3.98-3.09z"/>
    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42A11.97 11.97 0 0 0 12 0 11.99 11.99 0 0 0 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z"/>
  </svg>
);

// Sections that should not be offered by the "Resume" banner.
const TRANSIENT_SECTIONS = ['get-help', 'triage', 'sitemap'];

export default function MainApp() {
  const { user, login, register, logout, isAdmin, isVerified, isPending, loginWithGoogle, completeGoogleLogin } = useAuth();
  const location = useLocation();

  // Google OAuth callback: detect session_id in the URL fragment synchronously
  // during render (before any auth check consumes state), then exchange it once.
  const googleSessionId = location.hash?.includes('session_id=')
    ? location.hash.split('session_id=')[1]?.split('&')[0]
    : null;
  const googleProcessed = useRef(false);
  
  // Calculate if user is a verified buyer (for facet access)
  const isVerifiedBuyer = isAdmin ||
                          user?.verified_projects?.length > 0 || 
                          user?.project_verifications?.some(v => v.status === 'verified');

  // Membership state for the verify-ownership nudge (fresh from the canonical store)
  const [memberState, setMemberState] = useState(null); // 'none' | 'pending' | 'verified'
  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  useEffect(() => {
    if (!user) { setMemberState(null); return; }
    if (sessionStorage.getItem('fbc_verify_nudge_dismissed')) setNudgeDismissed(true);
    axios.get(`${API}/memberships/me`)
      .then((r) => {
        const ms = r.data.memberships || [];
        setMemberState(!ms.length ? 'none' : ms.some((m) => ['verified', 'group_admin'].includes(m.status)) ? 'verified' : ms.some((m) => m.status === 'pending') ? 'pending' : 'none');
      })
      .catch(() => setMemberState('none'));
  }, [user]);
  
  // Use custom hooks for reusable logic
  const { darkMode, toggleDarkMode } = useDarkMode();
  const { showScrollTop, scrollToTop, scrollToElement } = useScrollNavigation();
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [authTab, setAuthTab] = useState('login');
  const [sidebarCollapsed, setSidebarCollapsed] = useLayoutPref('sidebar_collapsed', false);
  
  // Forgot password state
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetStep, setResetStep] = useState('request'); // 'request' | 'reset'
  
  // Find/Add Project dialog
  const [findProjectOpen, setFindProjectOpen] = useState(false);
  const [findPrefill, setFindPrefill] = useState(null);
  // "Request your project" from any screen opens the request form, pre-filled with the search text.
  useEffect(() => {
    const open = (e) => { setFindPrefill({ name: e.detail?.name || '' }); setFindProjectOpen(true); };
    window.addEventListener('fbc-request-project', open);
    return () => window.removeEventListener('fbc-request-project', open);
  }, []);
  
  // Active section for logged-in dashboard (persisted, with validation on restore)
  const ROLE_GATED_SECTIONS = {
    'admin': 'admin',
    'lms-admin': 'admin',
    'developer-approval': 'admin',
    'developer-console': 'developer',
    'dev-my-projects': 'developer',
    'dev-verifications': 'developer',
    'dev-post-updates': 'developer',
    'dev-issues': 'developer',
    'professional-dashboard': 'professional',
  };
  const [activeSectionRaw, setActiveSectionRaw] = useLayoutPref('active_section', 'home', {
    validate: (stored) => {
      if (typeof stored !== 'string' || !stored) return false;
      // Skip transient flows (triage, sitemap) — they shouldn't auto-resume.
      if (TRANSIENT_SECTIONS.includes(stored)) return false;
      // Skip role-gated sections when current user lacks that role.
      const required = ROLE_GATED_SECTIONS[stored];
      if (required === 'admin' && !isAdmin) return false;
      if (required === 'developer' && user?.role !== 'developer') return false;
      if (required === 'professional' && user?.role !== 'professional') return false;
      return true;
    }
  });
  // Old section ids resolve to their single new home in the site tree (lib/navTree.js).
  const activeSection = SECTION_REDIRECTS[activeSectionRaw] || activeSectionRaw;
  const setActiveSection = useCallback((s) => setActiveSectionRaw(SECTION_REDIRECTS[s] || s), [setActiveSectionRaw]);
  useSectionHistory(activeSection, setActiveSection);
  // Every new screen starts at the top (moves between My Project sections keep their own scroll-to-heading).
  const prevSectionRef = useRef(activeSection);
  useEffect(() => {
    const prev = prevSectionRef.current;
    prevSectionRef.current = activeSection;
    if (prev !== activeSection && !(prev?.startsWith('project-') && activeSection?.startsWith('project-'))) window.scrollTo(0, 0);
  }, [activeSection]);

  
  // Selected project ID for project-details view (persisted across reloads)
  const [selectedProjectId, setSelectedProjectId] = useLayoutPref('selected_project_id', null);

  // Deep links (?s= or ?section=, e.g. WhatsApp shares) are handled by useSectionHistory.
  
  // Viewer modals
  const [viewingVideo, setViewingVideo] = useState(null);
  const [viewingArticle, setViewingArticle] = useState(null);
  const [viewingNews, setViewingNews] = useState(null);
  const [socialProof, setSocialProof] = useState(null);
  // Community stats strip removed until real numbers exist; social-proof fetch kept for future re-enable.
  
  // Form state
  const [formData, setFormData] = useState({ email: '', password: '', name: '', role: 'buyer', phone: '' });

  // Refs for scrolling
  const homeRef = useRef(null);
  const dashboardRef = useRef(null);
  const findProjectRef = useRef(null);

  // Fetch social proof stats
  useEffect(() => {
    axios.get(`${API}/social-proof/global`).then(res => setSocialProof(res.data)).catch(() => {});
  }, [user]);

  // In-app navigation requests from deep components (e.g. paywall "Subscribe" buttons).
  useEffect(() => {
    const go = (e) => { if (typeof e.detail === 'string') { setActiveSection(e.detail); window.scrollTo(0, 0); } };
    window.addEventListener('fbc-nav', go);
    return () => window.removeEventListener('fbc-nav', go);
  }, [setActiveSection]);


  const trackedProjectId = activeSection?.startsWith('project') ? selectedProjectId : null;
  useEffect(() => { if (activeSection) trackView(activeSection, trackedProjectId); }, [activeSection, trackedProjectId]);

  // Track the most-recent non-home section so the Resume banner has something to offer.
  useEffect(() => {
    if (!activeSection || activeSection === 'home') return;
    if (TRANSIENT_SECTIONS.includes(activeSection)) return;
    try {
      localStorage.setItem('fbc_pref_last_visited_section', JSON.stringify(activeSection));
    } catch { /* noop */ }
  }, [activeSection]);

  // API error detail can be a string, an object, or a 422 validation array —
  // always normalize to a string before handing it to a toast/renderer.
  const errMsg = (err, fallback) => {
    const d = err?.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join('; ');
    if (d && typeof d === 'object') return d.message || JSON.stringify(d);
    return fallback;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(formData.email.trim().toLowerCase(), formData.password);
      toast.success('Welcome back!');
      setAuthDialogOpen(false);
    } catch (err) {
      toast.error(errMsg(err, 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (userData) => {
    setLoading(true);
    try {
      await register(userData);
      setAuthDialogOpen(false);
      if (sessionStorage.getItem('fbc_req') && activeSection === 'project-request') {
        toast.success('Account created! Tap "This is my project too" to join the request.');
      } else if (['buyer', 'buyer_group_admin'].includes(userData.role)) {
        toast.success('Account created! Last step: verify your flat ownership.');
        setActiveSection('verify-ownership');
        window.scrollTo(0, 0);
      } else {
        toast.success('Account created successfully!');
      }
    } catch (err) {
      toast.error(errMsg(err, 'Registration failed'));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Complete Google OAuth: exchange the fragment session_id for an FBC session,
  // then clean the URL. useRef guard prevents double-processing under StrictMode.
  useEffect(() => {
    if (!googleSessionId || googleProcessed.current) return;
    googleProcessed.current = true;
    (async () => {
      try {
        const u = await completeGoogleLogin(googleSessionId);
        toast.success(`Welcome${u?.name ? `, ${u.name.split(' ')[0]}` : ''}!`);
      } catch (err) {
        toast.error(errMsg(err, 'Google sign-in failed. Please try again.'));
      } finally {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleSessionId, completeGoogleLogin]);

  // Reset password token detection from URL (?token=... or ?reset_token=...)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token') || params.get('reset_token');
    if (token) {
      setResetToken(token);
      setForgotPasswordMode(true);
      setResetStep('reset');
      setAuthDialogOpen(true);
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [location.search]);

  const updateField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const openAuthDialog = (tab = 'login') => { 
    setAuthTab(tab); 
    setForgotPasswordMode(false);
    setResetStep('request');
    setAuthDialogOpen(true); 
  };
  // Sign-in requests from deep components (e.g. IntelLock "Sign in free").
  useEffect(() => {
    const open = (e) => openAuthDialog(typeof e.detail === 'string' ? e.detail : 'login');
    window.addEventListener('fbc-auth', open);
    return () => window.removeEventListener('fbc-auth', open);
  });


  // Forgot password handlers
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email: resetEmail });
      toast.success('Check your email for reset instructions');
      // In dev mode, we get the token back - in production this would be sent via email
      if (res.data.reset_token) {
        setResetToken(res.data.reset_token);
        setResetStep('reset');
      }
    } catch (err) {
      toast.error('Failed to request password reset');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token: resetToken, new_password: newPassword });
      toast.success('Password reset successful! Please login.');
      setForgotPasswordMode(false);
      setResetStep('request');
      setResetEmail('');
      setResetToken('');
      setNewPassword('');
      setAuthTab('login');
    } catch (err) {
      toast.error(errMsg(err, 'Failed to reset password'));
    } finally {
      setLoading(false);
    }
  };

  const handleScrollToTop = () => {
    scrollToTop();
    setActiveSection('home');
  };

  // Scroll to specific content section
  const scrollToSection = (sectionRef, sectionId) => {
    setActiveSection(sectionId);
    scrollToElement(sectionRef);
  };

  const scrollToDashboard = () => {
    // Set active section to first private section if on home
    if (activeSection === 'home') {
      setActiveSection('project');
    }
    scrollToElement(dashboardRef);
  };

  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    
    if (sectionId === 'home') {
      handleScrollToTop();
    } else {
      // Dashboard renders directly under the fixed header, so start each section at the top.
      setTimeout(() => window.scrollTo({ top: 0 }), 50);
    }
  };

  const swipeHint = useSwipeNav(activeSection, handleSectionChange);


  const verifyNudge = user && ['buyer', 'buyer_group_admin'].includes(user.role) && memberState === 'none' && !nudgeDismissed && activeSection !== 'verify-ownership' && (
    <div className="mb-4 flex items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-4 py-3" data-testid="verify-nudge-banner">
      <ShieldCheck className="h-5 w-5 text-amber-600 shrink-0" />
      <p className="text-sm flex-1"><b>One step left:</b> verify your flat ownership to unlock the Buyers' Group, Corpus Issues and a 30-day free trial of FBC Project Intelligence.</p>
      <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => setActiveSection('verify-ownership')} data-testid="verify-nudge-cta">Verify now</Button>
      <button type="button" aria-label="Dismiss" className="text-amber-700/60 hover:text-amber-800 text-lg leading-none px-1" onClick={() => { setNudgeDismissed(true); sessionStorage.setItem('fbc_verify_nudge_dismissed', '1'); }} data-testid="verify-nudge-dismiss">&times;</button>
    </div>
  );

  // Render dashboard content with Suspense for lazy-loaded components
  const renderDashboardContent = () => {
    const content = getSectionContent(activeSection, {
      user,
      isAdmin,
      isVerifiedBuyer,
      selectedProjectId,
      setActiveSection,
      setSelectedProjectId,
      setFindProjectOpen,
      openAuthDialog,
    });

    // Wrap in Suspense for lazy-loaded components
    return (
      <>
        {verifyNudge}
        <Suspense fallback={<SectionLoader message={`Loading ${activeSection}...`} />}>
          {content}
        </Suspense>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-background pb-16 lg:pb-0">
      {/* Sidebar Component - Desktop Only */}
      <Sidebar
        user={user}
        isAdmin={isAdmin}
        isDeveloper={isDeveloper(user)}
        selectedProjectId={selectedProjectId}
        activeSection={activeSection}
        sidebarCollapsed={sidebarCollapsed}
        darkMode={darkMode}
        onSectionChange={handleSectionChange}
        onScrollToTop={handleScrollToTop}
        onToggleDarkMode={toggleDarkMode}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        onLogout={logout}
        onOpenAuth={openAuthDialog}
      />
      
      {/* Mobile Bottom Navigation */}
      <SwipeHint hint={swipeHint} />
      <MobileBottomNav
        user={user}
        isAdmin={isAdmin}
        isDeveloper={isDeveloper(user)}
        isVerified={isVerified}
        selectedProjectId={selectedProjectId}
        isPending={isPending}
        activeSection={activeSection}
        darkMode={darkMode}
        onSectionChange={handleSectionChange}
        onScrollToTop={handleScrollToTop}
        onToggleDarkMode={toggleDarkMode}
        onLogout={logout}
        onOpenAuth={openAuthDialog}
        onOpenFindProject={() => setFindProjectOpen(true)}
      />
      
      {/* Main Content Area - Offset for sidebar on large screens; pt reserves the fixed 65px header */}
      <div className={`lg:ml-64 ${sidebarCollapsed ? 'lg:ml-16' : ''} transition-all duration-300 pt-[65px] lg:pt-0`}>
        {/* Fixed Header - Visible on all screens */}
        <header className="lg:hidden fixed top-0 left-0 right-0 lg:left-64 z-40 bg-background border-b transition-all duration-300">
          <div className="container mx-auto px-4 py-3 flex items-center justify-between">
            {/* Logo - phones/tablets only; on desktop the sidebar carries the brand */}
            <div className="flex items-center gap-3 cursor-pointer lg:hidden" onClick={handleScrollToTop} data-testid="header-logo">
              <div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center shrink-0">
                <span className="text-primary-foreground font-bold text-sm">FBC</span>
              </div>
              <div className="hidden sm:block">
                <div className="font-semibold text-sm whitespace-nowrap" style={{ fontFamily: 'Playfair Display, serif' }}>Flat Buyers' Connect</div>
              </div>
            </div>
            
            {/* Desktop spacer */}
            <div className="hidden lg:block" />
          
            <div className="flex items-center gap-2">
              {/* Dark mode toggle - desktop only (mobile has it in menu) */}
              <Button variant="ghost" size="sm" onClick={toggleDarkMode} className="hidden lg:flex">
                {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>
              
              {/* Auth buttons - Visible when not logged in */}
              {!user && (
                <>
                  <Button variant="ghost" size="sm" onClick={() => openAuthDialog('login')} data-testid="nav-sign-in" className="flex">Sign In</Button>
                </>
              )}
              
              {/* User avatar - when logged in on mobile */}
              {user && (
                <div className="flex items-center gap-2 lg:hidden">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <span className="text-primary-foreground font-bold text-xs">{user.name?.[0]}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

      {/* LANDING PAGE CONTENT - Only visible when on home */}
      {activeSection === 'home' && (
      <>
      {/* Resume where you left off — surfaces last-visited section for logged-in users */}
      <ResumeBanner
        user={user}
        onResume={(sectionId) => handleSectionChange(sectionId)}
      />
      {verifyNudge && <div className="container mx-auto px-4 mt-4 max-w-6xl">{verifyNudge}</div>}
      <PendingProofBanner onOpenVerify={() => setActiveSection('verify-ownership')} />
      {/* Corpus Fight pulse — surfaces the hot case for verified project members */}
      <CorpusPulseBanner
        user={user}
        onNavigate={(sectionId) => handleSectionChange(sectionId)}
      />
      {/* HERO SECTION - Calm Awareness Positioning */}
      <section ref={homeRef} className="relative min-h-[80vh] flex items-center pt-20">
        <div className="absolute inset-0 bg-cover bg-center z-0" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&q=85)', filter: 'brightness(0.25)' }} />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/40 to-background z-[1]" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
              <p className="text-sm sm:text-base text-white/80 mb-3">
                India's <span className="font-bold text-white">Buyers Led - Buyers First</span> Real Estate platform
              </p>

              <h1 className="text-3xl sm:text-4xl md:text-5xl font-semibold mb-3 leading-tight text-white tracking-wide" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="hero-tagline">
                {ETHOS_TAGLINE}
              </h1>
              <p className="text-lg sm:text-xl text-white/85 mb-5 font-light italic" data-testid="hero-subline">{ETHOS_SUBLINE}</p>

              <div className="max-w-2xl mx-auto mb-8">
                <p className="text-base md:text-lg text-white/75 leading-relaxed font-light">
                  FBC brings homebuyers together with the knowledge, information, tools and a shared space to understand their situation and take informed action.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {user ? (
                  <Button size="lg" className="text-base px-6 py-5" onClick={scrollToDashboard}>
                    Go to Dashboard <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                ) : (
                  <>
                    <Button 
                      size="lg" 
                      className="text-base px-6 py-5"
                      onClick={() => openAuthDialog('register')} 
                      data-testid="hero-join-btn"
                    >
                      <Users className="mr-2 h-5 w-5" /> Join the Community
                    </Button>
                    <Button size="lg" variant="outline" className="text-base px-6 py-5 bg-white/10 border-white/30 text-white hover:bg-white/20" onClick={() => handleSectionChange('browse-projects')} data-testid="hero-browse-projects">
                      Explore Projects <Search className="ml-2 h-5 w-5" />
                    </Button>
                  </>
                )}
              </div>
              
              {/* Project Search Bar */}
              {(
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="pt-10 max-w-2xl mx-auto"
                  data-testid="hero-find-project"
                >
                  <p className="text-white font-semibold text-lg mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>Find your project on FBC</p>
                  <p className="text-white/60 text-sm mb-3">Your project community starts with finding one another.</p>
                  <ProjectSearchBar 
                    onProjectSelect={(project) => {
                      handleSectionChange('project-rera-public');
                    }}
                    onOpenAuth={openAuthDialog}
                    user={user}
                  />
                </motion.div>
              )}
            </motion.div>
          </div>
        </div>
        <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10" animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
          <ChevronDown className="h-8 w-8 text-white/50" />
        </motion.div>
      </section>

      <WelcomeChecklist user={user} onNavigate={handleSectionChange} onFindProject={() => { setFindPrefill(null); setFindProjectOpen(true); }} />
      <FirstVisitSurvey user={user} />
      <EthosVerbs onNavigate={handleSectionChange} />
      <div className="container mx-auto px-4 max-w-4xl pb-6"><IsIsntCard className="shadow-sm" /></div>

      {/* AWARENESS SECTION - "What informed buyers know" */}
      <section className="py-16 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl font-semibold mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              What informed buyers know
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Simple things that make the difference between a confident purchase and a costly surprise.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
            {[
              {
                icon: Shield,
                title: 'Your rights under RERA',
                description: 'If your possession is delayed beyond the agreed date, Section 18 entitles you to interest on every rupee paid.',
                cta: 'Understand S.18',
                action: 'legal-services',
                color: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
                iconColor: 'text-amber-600',
                iconBg: 'bg-amber-100 dark:bg-amber-900/40'
              },
              {
                icon: AlertCircle,
                title: 'The subvention question',
                description: 'Developer paying your Pre-EMI? Know what happens if they stop — and what clauses protect you before you sign.',
                cta: 'Learn more',
                action: 'subvention-claims',
                color: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
                iconColor: 'text-red-600',
                iconBg: 'bg-red-100 dark:bg-red-900/40'
              },
              {
                icon: FileSearch,
                title: 'Before you commit',
                description: 'Financial health, regulatory approvals, title clarity, loan safety — four things every buyer should check.',
                cta: 'See the checklist',
                action: 'risk-landing',
                color: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
                iconColor: 'text-blue-600',
                iconBg: 'bg-blue-100 dark:bg-blue-900/40'
              },
              {
                icon: Users,
                title: 'Strength in community',
                description: 'Buyers from the same project sharing updates, concerns, and collective weight. You\'re not alone in this.',
                cta: 'Join your group',
                action: 'community-landing',
                color: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
                iconColor: 'text-green-600',
                iconBg: 'bg-green-100 dark:bg-green-900/40'
              }
            ].map((card, idx) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card 
                  className={`h-full ${card.color} border cursor-pointer hover:shadow-md transition-shadow group`}
                  onClick={() => handleSectionChange(card.action)}
                  data-testid={`awareness-card-${idx}`}
                >
                  <CardContent className="p-5">
                    <div className={`w-10 h-10 rounded-lg ${card.iconBg} flex items-center justify-center mb-4`}>
                      <card.icon className={`h-5 w-5 ${card.iconColor}`} />
                    </div>
                    <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">{card.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-3">{card.description}</p>
                    <span className="text-sm font-medium text-primary flex items-center gap-1 group-hover:gap-2 transition-all">
                      {card.cta} <ChevronRight className="h-3 w-3" />
                    </span>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* OUR STANCE - The honest picture */}
      <OurStancePanel onReadMore={() => handleSectionChange('about')} />

      {/* Community stats strip intentionally removed — will return with real, earned numbers. */}

      {/* S.18 CALCULATOR - Positioned as a free tool, not emergency */}
      <section className="py-10 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent border-b">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 max-w-4xl mx-auto">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <Calculator className="h-7 w-7 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Free: Section 18 Interest Calculator</h3>
                <p className="text-muted-foreground text-sm">
                  Possession delayed? Check what you may be entitled to — takes 30 seconds.
                </p>
              </div>
            </div>
            <Button 
              variant="outline"
              className="border-amber-400 text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20"
              onClick={() => handleSectionChange('legal-services')} 
              data-testid="s18-calculator-cta"
            >
              <Calculator className="mr-2 h-4 w-4" /> Try the Calculator
            </Button>
          </div>
        </div>
      </section>

      {/* PLATFORM VALUE SECTION - Clean, educational */}
      <section className="py-16 relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl font-semibold text-white mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              A platform built for both sides
            </h2>
            <p className="text-white/50 max-w-xl mx-auto">
              FBC works when buyers are informed and developers are transparent.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {/* Buyer Side */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="p-6 rounded-2xl bg-white/5 border border-white/10"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Home className="h-5 w-5 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-white">For Buyers</h3>
              </div>
              <div className="space-y-3">
                {[
                  'Connect with fellow buyers from your project',
                  'Understand your rights under RERA — before you need them',
                  'Access checklists for financial, legal, and approval assurances',
                  'Track your project\'s progress through community updates',
                  'Get collective support if things go off track',
                ].map((text, idx) => (
                  <div key={`buyer-benefit-${idx}`} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                    <p className="text-white/70 text-sm">{text}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Developer Side */}
            <motion.div
              initial={{ opacity: 0, x: 0 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="p-6 rounded-2xl bg-white/5 border border-white/10"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-emerald-400" />
                </div>
                <h3 className="text-xl font-semibold text-white">For Developers</h3>
              </div>
              <div className="space-y-3">
                {[
                  'Share project updates directly with your buyer community',
                  'Build trust through verified, transparent communication',
                  'Understand buyer concerns early — before they escalate',
                  'Showcase your commitment to timely delivery',
                ].map((text, idx) => (
                  <div key={`dev-benefit-${idx}`} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                    <p className="text-white/70 text-sm">{text}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
          
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="text-center mt-10"
          >
            <div className="inline-block px-6 py-3 rounded-full bg-white/5 border border-white/10">
              <p className="text-white/70 text-sm">
                <span className="text-primary">Awareness by Buyers</span>
                {' '}<span className="text-white/30">|</span>{' '}
                <span className="text-emerald-400">Transparency by Developers</span>
                {' '}<span className="text-white/30">|</span>{' '}
                <span className="text-white/90">Trust by Design</span>
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <EvidenceStrip onOpen={(to) => { setSelectedProjectId(DEFAULT_PROJECT_ID); setActiveSection(to); window.scrollTo({ top: 0 }); }} />

      {/* FIND YOUR PROJECT SECTION - Key CTA */}
      <section ref={findProjectRef} className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/10" />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-10"
            >
              <Badge variant="secondary" className="mb-4">
                <Building2 className="h-3 w-3 mr-2" /> Find Your Project
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Find or Add Your Project
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Connect with fellow buyers, track your project's progress, and stay informed about your rights.
              </p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Card className="border-2 border-primary/20 shadow-xl">
                <CardContent className="p-8">
                  <div className="grid md:grid-cols-2 gap-8">
                    {/* Find Project */}
                    <div className="text-center p-6 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                      <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-4">
                        <Search className="h-8 w-8 text-primary" />
                      </div>
                      <h3 className="text-xl font-semibold mb-2">Find My Project</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Search for your property and access buyer resources
                      </p>
                      <Button 
                        size="lg" 
                        onClick={() => {
                          if (!user) {
                            openAuthDialog('login');
                            toast.info('Please sign in to find your project');
                          } else {
                            setFindProjectOpen(true);
                          }
                        }}
                        data-testid="find-project-landing-btn"
                      >
                        <Search className="h-4 w-4 mr-2" /> Search Projects
                      </Button>
                    </div>
                    
                    {/* Add Project */}
                    <div className="text-center p-6 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                      <div className="w-16 h-16 mx-auto bg-amber-500/10 rounded-full flex items-center justify-center mb-4">
                        <Plus className="h-8 w-8 text-amber-600" />
                      </div>
                      <h3 className="text-xl font-semibold mb-2">Add New Project</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Project not listed? Submit it and help other buyers
                      </p>
                      <Button 
                        size="lg" 
                        variant="outline"
                        onClick={() => {
                          if (!user) {
                            openAuthDialog('register');
                            toast.info('Please register to add your project');
                          } else {
                            setFindProjectOpen(true);
                          }
                        }}
                        data-testid="add-project-landing-btn"
                      >
                        <Plus className="h-4 w-4 mr-2" /> Add Project
                      </Button>
                    </div>
                  </div>
                  
                  <Separator className="my-6" />
                  
                  <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4 text-green-600" />
                      <span>Verified Information</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-blue-600" />
                      <span>Buyer Community</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BarChart3 className="h-4 w-4 text-purple-600" />
                      <span>Buyer Resources</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      </>
      )} {/* End of Landing Page Content conditional */}

      {/* DASHBOARD SECTION - Renders when NOT on home */}
      {activeSection !== 'home' && (
        <section ref={dashboardRef} className="pb-8 min-h-screen pt-2 lg:pt-4">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              {/* Section header with navigation hint */}
              <div className="flex items-center justify-between gap-2 mb-3 sticky top-[65px] lg:top-0 z-[35] bg-background py-1 -mx-4 px-2 sm:px-4 border-b border-border/30">
                <SectionTrail activeSection={activeSection} onNavigate={handleSectionChange}
                  fallbackLabel={SECTION_LABELS[activeSection] || [...publicSections, ...privateSections].find(s => s.id === activeSection)?.label} />
                {!user && (
                  <Button variant="outline" size="sm" className="hidden lg:inline-flex h-7 min-h-0 text-xs shrink-0" onClick={() => openAuthDialog('login')} data-testid="visitor-signin-dashboard">
                    Sign in
                  </Button>
                )}
              </div>
              {activeSection !== 'verify-ownership' && (
                <div className="-mx-4 mb-4"><PendingProofBanner onOpenVerify={() => setActiveSection('verify-ownership')} /></div>
              )}
              
              {/* Dashboard content - always render based on activeSection */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeSection?.startsWith('project-') ? 'project-trunk' : activeSection}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, transition: { duration: 0 } }}
                  transition={{ duration: 0.15 }}
                >
                  {activeSection === 'home' ? (
                    <div className="text-center py-16 text-muted-foreground">
                      <Home className="h-16 w-16 mx-auto mb-4 opacity-30" />
                      <h3 className="text-xl font-medium mb-2">You're viewing the public content above</h3>
                      <p className="text-sm max-w-md mx-auto mb-6">
                        Use the menu to navigate to your private sections, or open the Knowledge Hub for news, guidance and buyer resources.
                      </p>
                      <div className="flex flex-wrap justify-center gap-2">
                        {privateSections.map(section => (
                          <Button 
                            key={section.id} 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleSectionChange(section.id)}
                          >
                            <section.icon className="h-4 w-4 mr-2" />
                            {section.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    renderDashboardContent()
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </section>
      )}


      {/* FOOTER */}
      <footer className="py-10 border-t">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3 cursor-pointer" onClick={handleScrollToTop}>
              <div className="w-9 h-9 bg-primary rounded-sm flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-sm">FBC</span>
              </div>
              <div>
                <div className="font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>Flat Buyers' Connect</div>
                <div className="text-xs text-muted-foreground">Trust & Diligence by Design</div>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <button 
                onClick={() => setActiveSection('about')}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                data-testid="footer-about"
              >
                Our Stance
              </button>
              <button 
                onClick={() => setActiveSection('sitemap')}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                data-testid="footer-sitemap"
              >
                Site Map
              </button>
              <span className="text-muted-foreground">|</span>
              <button onClick={() => setActiveSection('developer-register')} className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-developers">
                For Developers
              </button>
              <span className="text-muted-foreground">|</span>
              <p className="text-sm text-muted-foreground">© {new Date().getFullYear()} Flat Buyers' Connect. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>
      </div>  {/* End of main content wrapper (lg:ml-64) */}

      {/* Scroll to Top Button */}
      <ScrollToTopButton show={showScrollTop} onClick={handleScrollToTop} />

      {/* Auth Dialog */}
      <Dialog open={authDialogOpen} onOpenChange={(open) => {
        setAuthDialogOpen(open);
        if (!open) {
          setForgotPasswordMode(false);
          setResetStep('request');
        }
      }}>
        <DialogContent className="sm:max-w-md max-h-[90dvh] overflow-y-auto gap-3">
          <DialogHeader className="sticky -top-6 -mt-6 pt-6 bg-background pb-1 z-10">
            <div className={`items-center justify-center gap-2 mb-1 ${authTab === 'register' && !forgotPasswordMode ? 'hidden' : 'flex'}`}>
              <div className="w-9 h-9 bg-primary rounded-sm flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-sm">FBC</span>
              </div>
            </div>
            <DialogTitle className="text-center text-xl sm:text-2xl" style={{ fontFamily: 'Playfair Display, serif' }}>
              {forgotPasswordMode 
                ? (resetStep === 'request' ? 'Reset Password' : 'Create New Password')
                : (authTab === 'login' ? 'Welcome Back' : 'Join FBC')}
            </DialogTitle>
          </DialogHeader>
          
          {forgotPasswordMode ? (
            <div className="space-y-4">
              {resetStep === 'request' ? (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <p className="text-sm text-muted-foreground text-center">
                    Enter your email address and we'll send you instructions to reset your password.
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="reset-email">Email</Label>
                    <Input 
                      id="reset-email" 
                      type="email" 
                      placeholder="you@example.com" 
                      value={resetEmail} 
                      onChange={(e) => setResetEmail(e.target.value)} 
                      data-testid="reset-email-input"
                      required 
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="send-reset-button">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Send Reset Instructions
                  </Button>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    className="w-full" 
                    onClick={() => setForgotPasswordMode(false)}
                    data-testid="back-to-signin-button"
                  >
                    Back to Sign In
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <p className="text-sm text-muted-foreground text-center">
                    Enter your new password below.
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="new-password">New Password</Label>
                    <Input 
                      id="new-password" 
                      type="password" 
                      placeholder="Enter new password (min 6 characters)" 
                      value={newPassword} 
                      onChange={(e) => setNewPassword(e.target.value)} 
                      data-testid="new-password-input"
                      required 
                      minLength={6}
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="reset-password-button">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Reset Password
                  </Button>
                </form>
              )}
            </div>
          ) : (
            <>
            <div className="space-y-3 mb-4">
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={loginWithGoogle}
                data-testid="google-signin-button"
              >
                <GoogleIcon /> Continue with Google
              </Button>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <Separator className="flex-1" />
                <span>or continue with email</span>
                <Separator className="flex-1" />
              </div>
            </div>
            <Tabs value={authTab} onValueChange={setAuthTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
              </TabsList>
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input id="login-email" type="email" placeholder="you@example.com" value={formData.email} onChange={(e) => updateField('email', e.target.value)} data-testid="login-email" autoComplete="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="email" required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <div className="relative">
                      <Input id="login-password" type={showLoginPassword ? "text" : "password"} placeholder="Enter your password" value={formData.password} onChange={(e) => updateField('password', e.target.value)} data-testid="login-password" autoComplete="current-password" className="pr-10" required />
                      <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0 h-full px-3 hover:bg-transparent" onClick={() => setShowLoginPassword(!showLoginPassword)}>
                        {showLoginPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                      </Button>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button 
                      type="button" 
                      variant="link" 
                      className="px-0 text-sm text-muted-foreground hover:text-primary"
                      onClick={() => setForgotPasswordMode(true)}
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </Button>
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Sign In
                  </Button>
                </form>
              </TabsContent>
              <TabsContent value="register">
                <RegistrationForm 
                  onRegister={handleRegister}
                  onCancel={() => setAuthTab('login')}
                  loading={loading}
                />
              </TabsContent>
            </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Content Viewer Modals */}
      <VideoPlayerModal video={viewingVideo} onClose={() => setViewingVideo(null)} />
      <ArticleViewerModal article={viewingArticle} onClose={() => setViewingArticle(null)} />
      <NewsViewerModal news={viewingNews} onClose={() => setViewingNews(null)} />
      
      {/* Find/Add Project Dialog */}
      <EthosAckGate />
      <FindAddProject 
        isOpen={findProjectOpen} 
        prefill={findPrefill}
        onClose={() => setFindProjectOpen(false)}
        onRequireAuth={(tab) => {
          setFindProjectOpen(false);
          openAuthDialog(tab);
        }}
      />
    </div>
  );
}
